import React, { useCallback, useState } from 'react';
import * as Icons from 'react-feather';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SKAlerts, { CreateAlert } from '../../../shared/common-alerts/SKAlerts';
import { ReactComponent as SortArrow } from '../../../shared/img/chevron-up-down.svg';
import ProductSubCategoryModel from './models/ProductSubCategoryModel';
import ProductSubCategoryModal from './ProductSubCategoryModal';
import { getStorageValues } from '../../../shared/CommonFunctions';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { logAuditEvent } from '../../../utils/auditLogger';
import { buildProductSubCategorySavePayload, mapProductSubCategoryFromApi } from './utils/mapProductSubCategory';
import { toBooleanFromBit } from './utils/productSubCategoryHelpers';
import { validateProductSubCategory } from './productSubCategoryValidation';

function ProductSubCategoryGrid({
  SubCategoryData,
  fetchSubCategories,
  UserPermissions = {},
  moduleName = 'Product Sub Category',
}) {
  const [actionType, setActionType] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [subCategoryModel, setSubCategoryModel] = useState(new ProductSubCategoryModel());
  const [filterText, setFilterText] = useState('');
  const [alert, setAlert] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const isRowActive = (row) => toBooleanFromBit(row?.IsActive, false);

  const closeModal = () => {
    setIsOpen(false);
    fetchSubCategories();
  };

  const rows = Array.isArray(SubCategoryData)
    ? SubCategoryData.filter((item) => {
        const search = filterText.toLowerCase();
        return (
          (item.CategoryName && item.CategoryName.toLowerCase().includes(search)) ||
          (item.SubCategoryName && item.SubCategoryName.toLowerCase().includes(search)) ||
          (item.Slug && item.Slug.toLowerCase().includes(search)) ||
          (item.ImageUrl && item.ImageUrl.toLowerCase().includes(search))
        );
      })
    : [];

  const loadSubCategoryDetail = async (subCategoryId) => {
    const response = await axios.get(`${getApiBaseUrl()}/ec-product-sub-category/getById`, {
      params: { SubCategoryId: subCategoryId },
    });
    return mapProductSubCategoryFromApi(response.data?.data);
  };

  const openActionType = useCallback(
    async (row, name) => {
      const detail = row?.SubCategoryName ? `Sub Category: ${row.SubCategoryName}` : '';
      logAuditEvent(moduleName, `${name} Click`, `${name} clicked. ${detail}`.trim());
      setLoadingDetail(true);
      try {
        const full = row?.SubCategoryId ? await loadSubCategoryDetail(row.SubCategoryId) : row;
        setSubCategoryModel(full || new ProductSubCategoryModel());
        setActionType(name);
        setIsOpen(true);
      } catch (err) {
        console.error(err);
        CreateAlert('Failed to load sub category details', 'Error', setAlert);
      } finally {
        setLoadingDetail(false);
      }
    },
    [moduleName]
  );

  const toggleActive = useCallback(
    async (row) => {
      if (UserPermissions.EditModule !== 1 || !row?.SubCategoryId) return;

      const nextActive = !isRowActive(row);
      setTogglingId(row.SubCategoryId);
      logAuditEvent(
        moduleName,
        'Toggle Active',
        `Sub category ${row.SubCategoryName} set to ${nextActive ? 'active' : 'inactive'}`
      );

      try {
        const payload = buildProductSubCategorySavePayload(
          { ...mapProductSubCategoryFromApi(row), IsActive: nextActive },
          getStorageValues('userId')
        );
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-sub-category/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(
            result.message || `Sub category ${nextActive ? 'activated' : 'deactivated'} successfully!!`,
            'Success',
            setAlert
          );
          fetchSubCategories();
        } else {
          CreateAlert(result?.message || 'Failed to update sub category status!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update sub category status!!', 'Error', setAlert);
      } finally {
        setTogglingId(null);
      }
    },
    [UserPermissions.EditModule, fetchSubCategories, moduleName]
  );

  const onSubmit = async (e, data) => {
    if (e.target.name === 'Edit') {
      const payload = buildProductSubCategorySavePayload(data, getStorageValues('userId'));
      if (!validateProductSubCategory(payload, setAlert)) return;
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-sub-category/save`, payload);
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Sub category updated successfully!!', 'Success', setAlert);
          fetchSubCategories();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to update sub category!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to update sub category!!', 'Error', setAlert);
      }
    } else if (e.target.name === 'Delete') {
      try {
        const response = await axios.post(`${getApiBaseUrl()}/ec-product-sub-category/delete`, {
          SubCategoryId: subCategoryModel.SubCategoryId,
          UserId: getStorageValues('userId'),
        });
        const result = response.data?.data;
        if (result?.success === 1) {
          CreateAlert(result.message || 'Sub category deleted successfully!!', 'Success', setAlert);
          fetchSubCategories();
          closeModal();
        } else {
          CreateAlert(result?.message || 'Failed to delete sub category!!', 'Warning', setAlert);
        }
      } catch (error) {
        console.error(error);
        CreateAlert('Failed to delete sub category!!', 'Error', setAlert);
      }
    }
  };

  const columns = [
    {
      name: (
        <div className="titlename">
          <span>Category</span>
        </div>
      ),
      selector: (row) => row.CategoryName,
      cell: (row) => <span title={row.CategoryName}>{row.CategoryName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Sub Category</span>
        </div>
      ),
      selector: (row) => row.SubCategoryName,
      cell: (row) => <span title={row.SubCategoryName}>{row.SubCategoryName}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Slug</span>
        </div>
      ),
      selector: (row) => row.Slug,
      cell: (row) => <span title={row.Slug}>{row.Slug}</span>,
      sortable: true,
      sortIcon: <SortArrow />,
    },
    {
      name: (
        <div className="titlename">
          <span>Active</span>
        </div>
      ),
      cell: (row) => (
        <div className="status-toggle modal-status">
          <input
            type="checkbox"
            id={`subcategory-active-${row.SubCategoryId}`}
            className="check"
            checked={isRowActive(row)}
            disabled={UserPermissions.EditModule !== 1 || togglingId === row.SubCategoryId}
            onChange={() => toggleActive(row)}
          />
          <label htmlFor={`subcategory-active-${row.SubCategoryId}`} className="checktoggle" />
        </div>
      ),
      width: '100px',
    },
  ];

  if (UserPermissions.ViewModule === 1 || UserPermissions.EditModule === 1 || UserPermissions.DeleteModule === 1) {
    columns.push({
      name: <div className="titlename">Actions</div>,
      cell: (row) => (
        <div className="edit-delete-action">
          {UserPermissions.ViewModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'View')}>
              <Icons.Eye className="action-eye" />
            </Link>
          ) : null}
          {UserPermissions.EditModule === 1 ? (
            <Link className="me-2 p-2" to="" onClick={() => openActionType(row, 'Edit')}>
              <Icons.Edit className="feather-edit" />
            </Link>
          ) : null}
          {UserPermissions.DeleteModule === 1 ? (
            <Link className="confirm-text p-2" to="" onClick={() => openActionType(row, 'Delete')}>
              <Icons.Trash2 className="feather-trash-2" />
            </Link>
          ) : null}
        </div>
      ),
      center: 'true',
      width: '150px',
    });
  }

  return (
    <>
      <SKAlerts alert={alert} />
      <div className="card table-list-card">
        <div className="card-body">
          <div className="table-top">
            <div className="search-set">
              <div className="search-input">
                <button type="button" className="btn btn-searchset">
                  <Icons.Search className="feather-search" />
                </button>
                <input
                  id="search-subcategory"
                  type="text"
                  className="form-control form-control-sm custom-search-input"
                  placeholder="Search"
                  aria-label="Search Input"
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </div>
            </div>
          </div>
          <DataTable
            className="table-responsive product-list"
            columns={columns}
            data={rows}
            pagination
            defaultSortField="SubCategoryName"
            sortIcon={<SortArrow />}
            persistTableHead
            progressPending={loadingDetail}
          />
        </div>
      </div>
      <ProductSubCategoryModal
        show={modalIsOpen}
        UserAction={actionType}
        onClose={closeModal}
        selectedRow={subCategoryModel}
        onSubmit={onSubmit}
        moduleName={moduleName}
      />
    </>
  );
}

export default ProductSubCategoryGrid;
